/**
 * React Query hooks for Clubs
 * 
 * Standard pattern for club data fetching and mutations.
 * Reference implementation for Issue #173.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clubsApi, type CreateClubPayload, type UpdateClubPayload, type CreateClubResponse } from '../lib/api/clubs';

/**
 * Fetch all clubs for current user
 * 
 * Query Key: ['clubs']
 * Returns: Array of clubs with user's role in each
 */
export function useClubs() {
  return useQuery({
    queryKey: ['clubs'],
    queryFn: clubsApi.listMyClubs,
    staleTime: 1000 * 60 * 2, // 2 minutes - clubs list doesn't change frequently
  });
}

/**
 * Fetch single club by slug
 * 
 * Query Key: ['club', slug]
 * Returns: Club details with user's role
 */
export function useClub(slug: string) {
  return useQuery({
    queryKey: ['club', slug],
    queryFn: () => clubsApi.getClub(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch single club by ID
 * 
 * Query Key: ['club', 'id', id]
 * Returns: Club details with user's role
 */
export function useClubById(id: string) {
  return useQuery({
    queryKey: ['club', 'id', id],
    queryFn: () => clubsApi.getClubById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Create a new club
 * 
 * On success: Invalidates ['clubs'] to refresh the list
 */
export function useCreateClub() {
  const queryClient = useQueryClient();
  
  return useMutation<CreateClubResponse, Error, CreateClubPayload>({
    mutationFn: (payload: CreateClubPayload) => clubsApi.createClub(payload),
    onSuccess: () => {
      // Refresh clubs list after creating new club
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
    },
  });
}

/**
 * Update club details
 * 
 * On success: Invalidates both ['clubs'] list and specific ['club', slug] query
 */
export function useUpdateClub(slug: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: UpdateClubPayload) => clubsApi.updateClub(slug, payload),
    onSuccess: () => {
      // Refresh both the list and the specific club
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
    },
  });
}

/**
 * Delete a club
 * 
 * On success: Invalidates ['clubs'] list
 */
export function useDeleteClub() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (slug: string) => clubsApi.deleteClub(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
    },
  });
}

/**
 * Join a club
 * 
 * On success: Invalidates both ['clubs'] list and specific ['club', slug] query
 */
export function useJoinClub() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (slug: string) => clubsApi.joinClub(slug),
    onSuccess: (_, slug) => {
      // Refresh both the list and the specific club (role will change)
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
    },
  });
}

/**
 * Leave a club
 * 
 * On success: Invalidates both ['clubs'] list and specific ['club', slug] query
 */
export function useLeaveClub() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (slug: string) => clubsApi.leaveClub(slug),
    onSuccess: (_, slug) => {
      // Refresh both the list and the specific club (role will change)
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
    },
  });
}

/**
 * Transfer club ownership
 * 
 * On success: Invalidates both ['clubs'] list and specific club queries
 */
export function useTransferOwnership(clubId: string, slug: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (newOwnerId: string) => 
      clubsApi.transferOwnership(clubId, { new_owner_id: newOwnerId }),
    onSuccess: () => {
      // Refresh both the list and the specific club (ownership changed)
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
      queryClient.invalidateQueries({ queryKey: ['clubMembers', slug] });
    },
  });
}
